import { MessageOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Point,
    SubtaskFacings,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";
import { uniqueByKey } from "tm-shared/utils";

import {
    CommentSelectModal,
    TaskInfoModal,
    useActiveScrollNavigator,
    useStickyFilteredItems,
    useSubtaskConfigSelector,
    useSubtaskFacingsSelector,
} from "@/domains/tasks";
import { useAppDispatch } from "@/hooks/redux";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { OutlineIconButton } from "@/shared/ui/OutlineIconButton";
import { PrimaryText } from "@/shared/ui/Typography";
import { patchFacingsEntity } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledList } from "../Styled";
import { FacingTask } from "./FacingTask";
import { StyledBottomSheetTop, StyledFacingsMainContainer } from "./Styled";

interface FacingsProps {
    subtask: SubtaskFacings;
    point: Point;
    task: TaskAssortmentMatrix;
}

export const Facings: FC<FacingsProps> = ({ subtask, point, task }) => {
    const dispatch = useAppDispatch();

    const pointId = point.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;
    const contractId = subtask.contractId;

    const previousActiveFacingRef = useRef<string | null>(null);

    const [modal, setModal] = useState<"comment" | "taskInfo" | null>(null);

    const facingsData = useSubtaskFacingsSelector({
        taskId,
        subtaskId,
    });

    const { activeItem: activeFacing, showUncompleted: showUncompletedFilter } =
        useSubtaskConfigSelector({
            pointId,
            taskId,
            subtaskId,
        });

    const activePayload = useMemo(
        () =>
            activeFacing
                ? {
                      id: activeFacing,
                      payload: facingsData?.facings?.[activeFacing] ?? null,
                  }
                : null,
        [activeFacing, facingsData],
    );

    const currentFacings = useMemo(
        () => uniqueByKey(subtask.context.facings, "finGroup"),
        [subtask.context.facings],
    );

    const { filteredItems: filteredFacings } = useStickyFilteredItems({
        items: currentFacings,
        showUncompleted: showUncompletedFilter ?? false,
        getItemId: (facing) => facing.finGroup,
        payloadsMap: facingsData?.facings ?? {},
        isIncomplete: (payload) => !payload || payload.amount === null,
    });

    const payloadsMapForCompletion = useMemo(
        () =>
            Object.entries(facingsData?.facings ?? {}).reduce(
                (acc, [finGroup, p]) => ({
                    ...acc,
                    [finGroup]: {
                        comment: p.comment,
                        status:
                            p.amount === null
                                ? TaskStatus.Incomplete
                                : TaskStatus.Completed,
                    },
                }),
                {},
            ),
        [facingsData?.facings],
    );

    const resetCommentIfNeeded = useCallback(
        (finGroup: string) => {
            const facingPayload = facingsData?.facings?.[finGroup];

            if (!facingPayload) return;

            const hasNoAmount =
                facingPayload.amount === null ||
                facingPayload.amount === undefined;

            if (hasNoAmount && facingPayload.comment) {
                dispatch(
                    patchFacingsEntity({
                        taskId,
                        subtaskId,
                        contractId,
                        finGroup,
                        patch: { comment: "" },
                    }),
                );
            }
        },
        [facingsData?.facings, dispatch, taskId, subtaskId, contractId],
    );

    const handleCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    }, [dispatch, pointId, taskId, subtaskId]);

    const handleOpenCommentModal = useCallback(() => {
        if (!activePayload) return;
        setModal("comment");
    }, [activePayload]);

    const handleOpenTaskInfoModal = () => {
        setModal("taskInfo");
    };

    const handleChangeActiveFacing = useCallback(
        (finGroup: string | null) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    subtaskId,
                    value: finGroup,
                }),
            );
        },
        [dispatch, pointId, taskId, subtaskId],
    );

    const onChangeFact = useCallback(
        (finGroup: string, value: number | null) => {
            dispatch(
                patchFacingsEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    finGroup,
                    patch: { amount: value },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId],
    );

    const handleChangeComment = useCallback(
        (newComment: string) => {
            if (!activePayload) return;

            dispatch(
                patchFacingsEntity({
                    taskId,
                    subtaskId,
                    finGroup: activePayload.id,
                    contractId,
                    patch: { comment: newComment },
                }),
            );
        },
        [dispatch, taskId, subtaskId, activePayload, contractId],
    );

    useEffect(() => {
        const previousFacing = previousActiveFacingRef.current;

        if (previousFacing && activeFacing !== previousFacing) {
            resetCommentIfNeeded(previousFacing);
        }

        previousActiveFacingRef.current = activeFacing ?? null;
    }, [activeFacing, resetCommentIfNeeded]);

    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredFacings.map((p) => p.finGroup),
            activeId: activeFacing ?? null,
            onChangeActive: handleChangeActiveFacing,
        });

    useAssortmentMatrixCompletion({
        taskId,
        subtaskId,
        expectedCount: currentFacings.length,
        payloadsMap: payloadsMapForCompletion,
    });

    const isCommentButtonDisabled =
        activePayload?.payload?.amount === null ||
        activePayload?.payload?.amount === undefined;

    useEffect(() => {
        scrollToActive();
    }, [facingsData?.completed, scrollToActive]);

    return (
        <>
            <StyledFacingsMainContainer>
                <StyledList>
                    {filteredFacings.map((facingInfo) => {
                        const facingPayload =
                            facingsData?.facings?.[facingInfo.finGroup];
                        const isActive = facingInfo.finGroup === activeFacing;

                        return (
                            <div
                                key={facingInfo.finGroup}
                                ref={getItemRef(facingInfo.finGroup)}
                            >
                                <FacingTask
                                    finGroup={facingInfo.finGroup}
                                    plan={facingInfo.quantity}
                                    value={facingPayload?.amount ?? null}
                                    isActive={isActive}
                                    onChangeActive={handleChangeActiveFacing}
                                    payload={facingPayload}
                                    onChangeFact={onChangeFact}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    pointId={pointId}
                    currentCompleted={facingsData?.completed}
                />
            </StyledFacingsMainContainer>

            <BottomSheet
                open={!!activeFacing}
                onClose={handleCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={handleOpenTaskInfoModal}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>Кі-сть фейсів {activePayload?.id}</PrimaryText>

                    <Flex gap="20px" justify="center" align="center">
                        <OutlineIconButton
                            icon={<MessageOutlined />}
                            onClick={handleOpenCommentModal}
                            disabled={isCommentButtonDisabled}
                        />
                    </Flex>
                </StyledBottomSheetTop>
            </BottomSheet>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload?.payload?.comment ?? ""}
                />
            )}

            {modal === "taskInfo" && (
                <TaskInfoModal
                    onCloseModal={() => setModal(null)}
                    title={subtask.name || "Фейсінги"}
                    description={
                        subtask.description ||
                        "Введіть кількість побачених фейсінгів"
                    }
                />
            )}
        </>
    );
};
